import { UserOutlined, CalendarOutlined } from '@ant-design/icons';
import { CiShop } from 'react-icons/ci';
import { PiGasPump } from 'react-icons/pi';
import { Card } from 'antd';
import classNames from 'classnames';
import dayjs from 'dayjs';

import {
  Check2Circle,
  ExclamationTriangle,
  GeoAlt,
  PencilSquare,
  Trash3,
} from 'react-bootstrap-icons';
import DefaultButton from '../../../../../components/buttons/DefaultButton';
import PrimaryButton from '../../../../../components/buttons/PrimaryButton';
import { PermissionEnum } from '../../../../../enums/PermissionEnum';
import { TaskPriority, TaskStatus } from '../../../timelineItem';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useTaskCompletionStatus } from '../../../../../hooks/task/useTaskCompletionStatus';

import { useEffect, useState, useMemo } from 'react';
import { MdKeyboardArrowDown } from 'react-icons/md';
import { BsGeoAlt } from 'react-icons/bs';
import { IoDuplicateOutline } from 'react-icons/io5';

import { FaArrowUpRightFromSquare } from 'react-icons/fa6';
import ViewRecordedPricesModal from './ViewRecordedPricesModal';
import MonitoringVisitsNote from './MonitoringVisitsNote';
import TaskLocationModal from '../../../../../components/modal/TaskLocationModal';
import Tag from '../../../../../components/tag/Tag';
import { useNotificationContext } from '../../../../../contexts/NotificationContext';
import { LegalMetroTaskTypeEnum, TaskTypeEnum } from '../../../../../enums/TaskTypeEnum';
import ViewRecordedMonitoringVisitModal from '../../../../subtaskInspections/scaleVerificationInspections/ViewRecordedMonitoringVisit';
import ViewRecordedFuelDispenserVerificationModal from '../../../../subtaskInspections/fuelPumpVerificationInspections/ViewRecordedFuelDispenserVerification';
import ViewPrePackagedProductModal from '../../../../subtaskInspections/prePackagedProductVerify/ViewPrePackagedProductModal';
import { referenceDataSyncService } from '../../../../../services/referenceDataSyncService';
import { usePermissions } from '../../../../../hooks/usePermissions';

interface ITask {
  item: any;
  handleEdit: (refId: string) => void;
  getDeleteConfirmation: (refId: string) => void;
  handleMarkAsCompleted: (refId: string) => void;
  handleDuplicate: (id: number) => void;
  isOnline?: boolean;
}

const Task = (props: ITask) => {
  const { t } = useTranslation(['task']);
  const {
    item,
    handleEdit,
    getDeleteConfirmation,
    handleMarkAsCompleted,
    handleDuplicate,
    isOnline,
  } = props;

  const [isRecordedPriceModalOpen, seIsRecordedPriceModalOpen] = useState<boolean>(false);
  const [isRecordedVerificationModalOpen, setIsRecordedVerificationModalOpen] = useState<number>(0);
  const [isRecordedProductVerificationModalOpen, setIsRecordedProductVerificationModalOpen] =
    useState<boolean>(false);

  const [isLocationModalOpen, setIsLocationModalOpen] = useState<boolean>(false);
  const [selectedInspection, setSelectedInspection] = useState<any>(null);
  const [establishment, setEstablishement] = useState<null | string>(null);

  const { warningNotification } = useNotificationContext();

  const [prices, setPrices] = useState<
    {
      price: number | null;
      product: string;
      presentation?: string;
      status?: string;
      isNotFound?: boolean;
    }[]
  >([]);

  const isOverDue = (date: any) => {
    return dayjs(date).isBefore(dayjs(), 'day');
  };

  const closeRecordedPriceModal = () => {
    seIsRecordedPriceModalOpen(false);
  };

  const openLocationModal = (inspection: any) => {
    setSelectedInspection(inspection);
    setIsLocationModalOpen(true);
  };

  const closeLocationModal = () => {
    setIsLocationModalOpen(false);
    setSelectedInspection(null);
  };

  const [showInspectionList, setShowInspectionList] = useState<boolean>(false);

  const { hasPermission } = usePermissions();

  const navigate = useNavigate();

  const taskTypeKey = item?.taskTypeKey;
  const subTaskTypeKey = item?.subTaskTypeKey;

  const startPriceRecord = (taskData: any) => {
    isOverDue(Number(item?.dueDate)) && warningNotification(t('overdue'));

    const tempTaskData = {
      assignees: taskData?.assignees?.map((assignee: any) => assignee?.id || assignee),
      basketId: taskData?.basketId,
      establishmentId: Number(taskData?.establishmentId),
      establishmentType: taskData?.establishmentType,
      locationId: Number(taskData?.locationId),
      priority: taskData?.priority,
      productRefIds: taskData?.productsRefIds || taskData?.productRefIds,
      refId: taskData?.refId,
      inspections: taskData?.inspections || [],
      prePackagedProductVerificationDetails:
        taskData?.inspections?.prePackagedProductVerificationDetails,
      triggeredFrom: 'overview',
      fuelStationName: taskData?.fuelStationName || null,
      status: taskData?.status || null,
    };

    if (taskTypeKey) {
      switch (taskTypeKey) {
        case TaskTypeEnum.CONSUMER_PRICE_INSPECTION:
          navigate('/inspector/price-record', { state: tempTaskData });
          break;
        case TaskTypeEnum.LEGAL_METROLOGY_INSPECTION:
          switch (subTaskTypeKey) {
            case LegalMetroTaskTypeEnum.SCALE_FORMAT_VERIFICATION:
              navigate('/inspector/scale-format-verification', { state: tempTaskData });
              break;
            case LegalMetroTaskTypeEnum.FUEL_DISPENSER_VERIFICATION:
              navigate('/inspector/fuel-dispenser-verification', { state: tempTaskData });
              break;
            case LegalMetroTaskTypeEnum.PRE_PACKAGED_PRODUCT_VERIFICATION:
              navigate('/inspector/pre-packaged-product-verification', { state: tempTaskData });
              break;
          }
          break;
      }
    } else {
      navigate('/inspector/price-record', { state: tempTaskData });
    }
  };

  useEffect(() => {
    const getEstablishmentById = async () => {
      const data = await referenceDataSyncService.getEstablishmentDataById(
        Number(item?.establishmentId)
      );
      setEstablishement(data[0]?.name);
    };
    item?.establishmentType && item?.establishmentId && getEstablishmentById();
  });

  const { canMarkAsCompleted, isMarkAsCompletedDisabled } = useTaskCompletionStatus({
    item,
    isOnline,
  });

  const inspections = useMemo(() => {
    return Array.isArray(item?.inspections) ? item.inspections : [];
  }, [item?.inspections]);

  const [viewInspectionData, setViewInspectionData] = useState(null);

  const closeRecordedProductVerificationInfo = () => {
    setIsRecordedProductVerificationModalOpen(false);
  };

  return (
    <>
      <ViewRecordedPricesModal
        isModalOpen={isRecordedPriceModalOpen}
        onCancel={closeRecordedPriceModal}
        prices={prices}
      />
      <ViewRecordedMonitoringVisitModal
        isModalOpen={isRecordedVerificationModalOpen == 1}
        formData={viewInspectionData}
        onCancel={() => setIsRecordedVerificationModalOpen(0)}
      />
      <ViewRecordedFuelDispenserVerificationModal
        isModalOpen={isRecordedVerificationModalOpen == 2}
        formData={viewInspectionData}
        onCancel={() => setIsRecordedVerificationModalOpen(0)}
      />
      <ViewPrePackagedProductModal
        isModalOpen={isRecordedProductVerificationModalOpen}
        formData={viewInspectionData}
        onCancel={() => closeRecordedProductVerificationInfo()}
      />
      <Card className="@container m-0 w-full rounded-lg border border-gray-200 p-0">
        <div className="space-y-2">
          {/* <div className="flex flex-row gap-2"> */}
          <div className="md-items-center mb-2 flex w-full flex-col items-start justify-between gap-2 md:flex-row">
            <div className="flex flex-row flex-wrap gap-2">
              {/* Task Type Tag */}
              <Tag
                tagType={taskTypeKey === TaskTypeEnum.LEGAL_METROLOGY_INSPECTION ? 'brown' : 'info'}
                filled={true}
              >
                {t(`taskTypeEnum.${taskTypeKey}`)}
              </Tag>

              {/* Legal Metrology Type Tag */}
              {item?.taskTypeKey === TaskTypeEnum.LEGAL_METROLOGY_INSPECTION && (
                <Tag
                  tagType={'default'}
                  /* tagType={
                    item?.subTaskTypeKey === LegalMetroTaskTypeEnum.SCALE_FORMAT_VERIFICATION
                      ? 'default'
                      : item?.subTaskTypeKey === LegalMetroTaskTypeEnum.PRE_PACKAGED_PRODUCT_VERIFICATION
                        ? 'purple'
                        : 'neutral'
                  } */
                  filled={false}
                >
                  {t(`legalMetroTaskTypeEnum.${subTaskTypeKey}`)}
                </Tag>
              )}
            </div>
            {/* Priority Tag */}
            <div className="flex md:items-center">
              <Tag
                tagType={
                  item?.priority === TaskPriority.HIGH
                    ? 'danger'
                    : item?.priority === TaskPriority.MEDIUM
                      ? 'warning'
                      : 'neutral'
                }
                filled={false}
              >
                {t(`${item?.priority}`)}
              </Tag>
            </div>
          </div>
          <div className="mb-4 flex flex-row flex-wrap items-center gap-2 md:gap-4">
            <div>
              <h2 className="text-md mb-0 mr-3 font-semibold md:text-lg">{`${item?.taskName}`}</h2>
              {item?.recurrenceTaskId && (
                <p className="m-0 mr-3 text-[10px] md:text-xs">
                  {t('generatedFromRecurringTask') + `${item?.recurrenceTaskName}`}
                </p>
              )}
            </div>
            <div className="flex flex-row gap-2">
              {/* <Tag
                tagType={
                  item.priority === TaskPriority.HIGH
                    ? 'danger'
                    : item.priority === TaskPriority.MEDIUM
                      ? 'warning'
                      : 'neutral'
                }
                filled={false}
              >
                {t(`${item.priority}`)}
              </Tag> */}

              <div className="flex items-center gap-2">
                <Tag
                  tagType={
                    item?.status === TaskStatus.COMPLETED
                      ? 'success'
                      : item?.status === TaskStatus.IN_PROGRESS
                        ? 'caution'
                        : item?.status === TaskStatus.DELAYED
                          ? 'danger'
                          : item?.status === TaskStatus.PENDING
                            ? 'warning'
                            : 'neutral'
                  }
                  filled={true}
                >
                  {t(`${item?.status}`)}
                </Tag>
              </div>
            </div>
          </div>
          {/* <p>{item?.description}</p> */}
        </div>

        <div className="md:text-md mb-2 mt-4 flex w-full flex-col items-start justify-between space-y-2 text-sm md:space-y-4">
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 sm:flex-row sm:gap-16 md:gap-4">
            {isOnline && (
              <div className="flex items-center gap-1">
                <UserOutlined className="shrink-0" /> {t('assignee')}:{' '}
                {item?.assignees.map((assignee: any) => assignee?.name).join(', ')}
              </div>
            )}
            <>
              {isOverDue(Number(item?.dueDate)) && item?.status !== TaskStatus.COMPLETED ? (
                <p className="w-fit rounded-md border border-red-500 px-2 text-red-500 md:px-4">
                  <ExclamationTriangle className="mb-1 mr-2 inline shrink-0" /> {t('due')} :{' '}
                  {dayjs(Number(item?.dueDate)).format('YYYY-MM-DD')}
                </p>
              ) : (
                <p className="flex items-center gap-1">
                  <CalendarOutlined className="shrink-0" /> {t('due')} :{' '}
                  {dayjs(Number(item?.dueDate)).format('YYYY-MM-DD')}
                </p>
              )}
            </>
          </div>
          {isOnline && (
            <p className="flex items-center gap-1">
              <BsGeoAlt className="shrink-0" />
              <span>
                {item?.municipality}, {item?.department}
              </span>
            </p>
          )}

          {establishment && (
            <p className="flex items-center gap-1">
              <CiShop className="shrink-0" />
              <span>
                {establishment}, {item?.establishmentType}
              </span>
            </p>
          )}

          {item?.fuelStationName && (
            <p className="flex items-center gap-1">
              <PiGasPump className="shrink-0" />
              <span>{item?.fuelStationName}</span>
            </p>
          )}
        </div>

        <div className="mt-10 flex flex-col flex-wrap gap-3 text-sm md:flex-row md:items-center md:gap-6">
          {/* Edit and Delete: Only for pending tasks */}
          {item?.status === 'pending' && (
            <>
              {hasPermission([PermissionEnum.UPDATE_INSPECTIONS]) && (
                <DefaultButton handleOnClick={() => handleEdit?.(item?.refId)}>
                  <div className="flex flex-row items-center justify-center gap-2">
                    <PencilSquare className="h-4 w-4 text-black" />
                    <p>{t('edit')}</p>
                  </div>
                </DefaultButton>
              )}
              {hasPermission([PermissionEnum.DELETE_INSPECTIONS]) && (
                <DefaultButton handleOnClick={() => getDeleteConfirmation?.(item?.refId)}>
                  <div className="flex flex-row items-center justify-center gap-2">
                    <Trash3 className="h-4 w-4 text-black" />
                    <p>{t('delete')}</p>
                  </div>
                </DefaultButton>
              )}
            </>
          )}

          {/* Start: Only for pending tasks */}
          {item?.status === 'pending' && hasPermission([PermissionEnum.RUN_INSPECTIONS]) && (
            <PrimaryButton handleOnClick={() => startPriceRecord(item)} hideBgGradient={true}>
              <div className="flex h-[18px] flex-row items-center justify-center gap-2">
                {t('start')}
              </div>
            </PrimaryButton>
          )}

          {/* Continue and Mark as Complete: Only for in_progress tasks */}
          {item?.status === 'in_progress' && hasPermission([PermissionEnum.RUN_INSPECTIONS]) && (
            <>
              <PrimaryButton handleOnClick={() => startPriceRecord(item)} hideBgGradient={true}>
                <div className="flex h-[18px] flex-row items-center justify-center gap-2 sm:justify-normal">
                  {t('continue')}
                </div>
              </PrimaryButton>
              {canMarkAsCompleted && (
                <DefaultButton
                  handleOnClick={() => isOnline && handleMarkAsCompleted?.(item?.refId)}
                  disabled={isMarkAsCompletedDisabled}
                >
                  <div className="flex flex-row items-center justify-center gap-2 sm:justify-normal">
                    <Check2Circle className="h-4 w-4 text-black" />
                    <p>{t('markAsCompleted')}</p>
                  </div>
                </DefaultButton>
              )}
            </>
          )}

          {isOnline && hasPermission([PermissionEnum.CREATE_INSPECTIONS]) && (
            <DefaultButton handleOnClick={() => handleDuplicate?.(Number(item?.id))}>
              <div className="flex flex-row items-center justify-center gap-2 sm:justify-normal">
                <IoDuplicateOutline className="h-4 w-4 text-black" />
                <p>{t('duplicate')}</p>
              </div>
            </DefaultButton>
          )}
        </div>

        {!isOnline && <MonitoringVisitsNote status={item?.status} />}

        {inspections.length > 0 && (
          <div
            className="mt-1 flex w-full cursor-pointer flex-row items-center gap-2 text-base md:mt-9"
            onClick={() => {
              setShowInspectionList((prev) => !prev);
            }}
          >
            <p className="text-nowrap text-sm">{t('viewInspections')}</p>
            <div className="h-[1px] w-full bg-gray-400" />
            <MdKeyboardArrowDown className="shrink-0 text-2xl" />
          </div>
        )}
        <div
          className={classNames(
            'space-y-6 overflow-hidden transition-all duration-150 ease-out md:space-y-4',
            { 'mt-6 max-h-[1000px] translate-y-0 opacity-100': showInspectionList },
            { 'max-h-0 -translate-y-2 opacity-0': !showInspectionList }
          )}
        >
          {[...inspections].reverse().map((inspection: any, _index: number) => (
            <div className="md:text-md grid grid-cols-1 items-start gap-1 text-sm md:grid-cols-2 lg:grid-cols-5">
              <h4>
                {t('doneBy')}:{' '}
                {typeof inspection?.doneBy === 'object'
                  ? inspection?.doneBy?.name || localStorage.getItem('name')
                  : inspection?.doneBy || localStorage.getItem('name')}
              </h4>
              <p className="col-span-1 !mt-0 lg:col-span-2">
                {t('date')}:{' '}
                {dayjs(Number(inspection?.date || inspection?.reportedTime)).format(
                  'YYYY-MM-DD HH:mm:ss A'
                )}
              </p>

              {item?.subTaskTypeId || item?.subTaskTypeKey ? (
                <div className="justify-self-start lg:col-span-2 lg:justify-self-end">
                  <div className="flex flex-row items-center gap-5">
                    <DefaultButton handleOnClick={() => {}} width="fit-content">
                      <div
                        className="flex flex-row items-center gap-2 text-xs md:text-sm"
                        onClick={() => {
                          switch (item?.taskTypeKey) {
                            case TaskTypeEnum.LEGAL_METROLOGY_INSPECTION:
                              switch (item?.subTaskTypeKey) {
                                case LegalMetroTaskTypeEnum.SCALE_FORMAT_VERIFICATION:
                                  setViewInspectionData(inspection.scaleVerificationDetails);
                                  setIsRecordedVerificationModalOpen(1);
                                  break;
                                case LegalMetroTaskTypeEnum.FUEL_DISPENSER_VERIFICATION:
                                  setViewInspectionData(
                                    inspection.fuelDispenserVerificationDetails
                                  );
                                  setIsRecordedVerificationModalOpen(2);
                                  break;
                                case LegalMetroTaskTypeEnum.PRE_PACKAGED_PRODUCT_VERIFICATION:
                                  setViewInspectionData(
                                    inspection.prePackagedProductVerificationDetails
                                  );
                                  setIsRecordedProductVerificationModalOpen(true);
                                  setPrices(inspection?.prices);
                                  break;
                                default:
                                  setViewInspectionData(inspection.scaleVerificationDetails);
                                  setIsRecordedProductVerificationModalOpen(true);
                                  setPrices(inspection?.prices);
                              }
                              break;
                            default:
                              setViewInspectionData(inspection.scaleVerificationDetails);
                              setIsRecordedProductVerificationModalOpen(true);
                              setPrices(inspection?.prices);
                          }
                        }}
                      >
                        {t('viewForm')} <FaArrowUpRightFromSquare />
                      </div>
                    </DefaultButton>
                  </div>
                </div>
              ) : (
                <div className="justify-self-start lg:col-span-2 lg:justify-self-end">
                  <div className="flex flex-row items-center gap-5">
                    <DefaultButton handleOnClick={() => {}} width="fit-content">
                      <div
                        className="flex flex-row items-center gap-2 text-xs md:text-sm"
                        onClick={() => {
                          seIsRecordedPriceModalOpen(true);
                          setPrices(inspection?.prices);
                        }}
                      >
                        {t('prices')} <FaArrowUpRightFromSquare />
                      </div>
                    </DefaultButton>

                    <button
                      className="!h-fit rounded-full border-[1px] border-default bg-white p-2 font-bold !leading-none text-default-font-color transition-shadow ease-in hover:shadow-md"
                      onClick={() => openLocationModal(inspection)}
                      title="View price locations on map"
                    >
                      <GeoAlt />
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </Card>

      <TaskLocationModal
        visible={isLocationModalOpen}
        onClose={closeLocationModal}
        inspection={selectedInspection}
      />
    </>
  );
};

export default Task;
